import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { NotificationsService } from 'app/services/notifications.service';
import { PromptuarySidebarService } from 'modules/promptuary/services/sidebar/promptuary-sidebar.service';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError, delay, map, retryWhen, take } from 'rxjs/operators';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { UnifiedPromptuaryService } from './../promptuary-form/unified-promptuary.service';
import { ServiceEvolutionQuality } from '../service-evolution-quality-banner/service-evolution-quality-banner.component';

@Component({
	selector: 'promptuary-register-modal',
	templateUrl: './promptuary-register-modal.component.html',
	styleUrls: ['./promptuary-register-modal.component.scss'],
})
export class PromptuaryRegisterModalComponent implements OnChanges, OnDestroy {
	@Input()
	quality: ServiceEvolutionQuality = 'default';
	@Input()
	from;
	@Input()
	mainType;
	@Input()
	appointment: IAppointment;
	@Input()
	appointmentId: string;
	@Input()
	form: FormGroup;
	@Input()
	promptuaryType: 'default' | 'crm';
	@Input()
	showModal: boolean;
	@Input()
	promptuary;
	@Input() name: string;

	@Output() onRegistered = new EventEmitter<any>();

	constructor(
		public actionTrackerService: ActionTrackerService,
		public notificationService: NotificationsService,
		public promptuaryService: PromptuaryService,
		public translateService: TranslateService,
		public unifiedPromptuaryService: UnifiedPromptuaryService,
		public promptuarySidebarService: PromptuarySidebarService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.showModal?.currentValue === true) {
			document.body.style.overflow = 'hidden';
			this.actionTrackerService.eventTracker(
				IAction.SCOPE_TYPE.Promptuary,
				IAction.SUBSCOPE_TYPE.Session,
				IAction.ITEM_TYPE.ModalRegister,
				IAction.INTERACTION_TYPE.Open,
				{
					specialist_type: this.mainType,
					from: this.from,
					...(this.isDefaultPromptuary() && {
						hasQuality: this.promptuaryService.hasQuality(this.quality),
					}),
				}
			);
		}
	}

	ngOnDestroy(): void {
		document.body.style.overflow = 'auto';
	}

	register() {
		this.trackRegisterButtonClick();
		this.unifiedPromptuaryService.toggleRegistering(true);
		this.closeModal(false);
		this.addBreadcrumb('Registering promptuary', {
			appointmentId: this.appointmentId,
			profileId: this.appointment.profileId,
			isCgiEmpty: this.form?.get('cgi')?.value?.length === 0,
		});

		//Registers the promptuary, the professional task and the cgi in parallel
		forkJoin([
			this.getRegisterPromptuaryObservable(),
			this.getTaskRegisterObservableOrNull(),
		]).subscribe(
			() => {},
			(error) => {
				console.error(error);
			}
		);
	}

	getRegisterPromptuaryObservable() {
		//Returns an observable that registers the promptuary
		return this.promptuaryService
			.registerDocument(
				this.appointmentId,
				this.appointment.profileId,
				this.isDefaultPromptuary()
					? this.promptuaryService.hasQuality(this.quality)
					: undefined
			)
			.pipe(
				map(() => {
					this.unifiedPromptuaryService.toggleRegistering(false);
					this.addBreadcrumb('Promptuary registered', {
						appointmentId: this.appointmentId,
						profileId: this.appointment.profileId,
					});
					this.notificationService.showSuccess(
						this.translateService.instant(
							this.promptuaryHasTask()
								? 'PROMPTUARY.INFO.SUCCESSFULLY_SAVED_TASK'
								: 'PROMPTUARY.INFO.SUCCESSFULLY_SAVED'
						)
					);

					this.trackSuccessfullyRegisteredPromptuary();
					if (this.isDefaultPromptuary()) {
						this.closeSidebarAndResetPromptuary(this.appointmentId);
						if (this.unifiedPromptuaryService.wasSpeechToTextUsed) {
							this.trackSpeechToText();
						}
					}
					this.onRegistered.emit();
				}),
				catchError((error) => {
					this.unifiedPromptuaryService.toggleRegistering(false);
					const promptuaryError = this.translateService.instant(
						'PROMPTUARY.DEFAULT_REGISTER_ERROR'
					);
					this.notificationService.showError(
						error?.messageI18n ? error.messageI18n : promptuaryError
					);
					this.throwError('Error registering promptuary');
					return throwError(error);
				})
			);
	}

	getTaskRegisterObservableOrNull() {
		const nullObservable = of({});
		const task = this.form.get('task');
		const taskDescription = task?.get('description')?.value;
		this.addBreadcrumb('Registering task', {
			appointmentId: this.appointmentId,
			profileId: this.appointment.profileId,
		});
		//Returns an observable that registers the professional task if the task description is not empty
		return taskDescription
			? this.promptuaryService
					.registerProfessionalTask(
						this.appointmentId,
						this.appointment.profileId
					)
					.pipe(
						map(() => {
							this.addBreadcrumb('Task registered', {
								appointmentId: this.appointmentId,
								profileId: this.appointment.profileId,
							});
						}),
						catchError((error) => {
							this.throwError('Error registering Task');
							return throwError(error);
						}),
						retryWhen((errors) => errors.pipe(delay(1000), take(2)))
					)
			: nullObservable;
	}

	isDefaultPromptuary(): boolean {
		return this.promptuaryType === 'default';
	}

	promptuaryHasTask(): boolean {
		return (
			this.promptuary.task &&
			(this.promptuary.task.description?.length > 0 ||
				this.promptuary.task.links?.length > 0)
		);
	}

	closeModal(shouldTrack = true): void {
		document.body.style.overflow = 'auto';

		if (shouldTrack) {
			this.actionTrackerService.eventTracker(
				IAction.SCOPE_TYPE.Promptuary,
				IAction.SUBSCOPE_TYPE.Session,
				IAction.ITEM_TYPE.ModalRegister,
				IAction.INTERACTION_TYPE.Cancel,
				{
					specialist_type: this.mainType,
					from: this.from,
					...(this.isDefaultPromptuary() && {
						hasQuality: this.promptuaryService.hasQuality(this.quality),
					}),
				}
			);
		}

		this.unifiedPromptuaryService.toggleRegisterModal(false);
	}

	closeSidebarAndResetPromptuary(id) {
		this.promptuarySidebarService.registerObservable.next(id);
		this.unifiedPromptuaryService.resetPromptuary();
		this.promptuarySidebarService.toggleSidebar(false);
	}

	trackRegisterButtonClick() {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			IAction.ITEM_TYPE.BtnRegister,
			IAction.INTERACTION_TYPE.Click,
			{
				specialist_type: this.mainType,
				from: this.from,
				...(this.isDefaultPromptuary() && {
					hasQuality: this.promptuaryService.hasQuality(this.quality),
				}),
			}
		);
	}

	trackSuccessfullyRegisteredPromptuary() {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			IAction.ITEM_TYPE.ModalRegister,
			IAction.INTERACTION_TYPE.Success,
			{
				specialist_type: this.mainType,
				from: this.from,
				...(this.isDefaultPromptuary() && {
					hasQuality: this.promptuaryService.hasQuality(this.quality),
				}),
				promptuary_outcome: this.promptuary.outcome,
				promptuary_care_plan:
					this.promptuary.outcomeForwardJourney &&
					this.promptuary.outcome === 'Encaminhamento de plano de cuidados'
						? this.promptuary.outcomeForwardJourney
						: null,
				promptuary_care_plan_sessions:
					this.promptuary.outcomeForwardJourneySessionNumber &&
					this.promptuary.outcome === 'Encaminhamento de plano de cuidados'
						? this.promptuary.outcomeForwardJourneySessionNumber
						: null,
				hasTask: Boolean(this.form.get('task')?.get('description')?.value),
				hasLinks: this.form.get('task')?.get('links')?.value.length > 0,
				promptuary_outcome_specialty: this.promptuary.outcomeSpecialty ?? null,
			}
		);
	}

	trackSpeechToText() {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.SpeechToText,
			IAction.ITEM_TYPE.BtnRegister,
			IAction.INTERACTION_TYPE.Click,
			{
				eventId: this.appointment.id,
				professionalId: this.appointment.professionalId,
				hasQuality: this.promptuaryService.hasQuality(this.quality),
				promptuary_outcome: this.promptuary.outcome,
				promptuary_care_plan:
					this.promptuary.outcomeForwardJourney &&
					this.promptuary.outcome === 'Encaminhamento de plano de cuidados'
						? this.promptuary.outcomeForwardJourney
						: null,
				promptuary_care_plan_sessions:
					this.promptuary.outcomeForwardJourneySessionNumber &&
					this.promptuary.outcome === 'Encaminhamento de plano de cuidados'
						? this.promptuary.outcomeForwardJourneySessionNumber
						: null,
			}
		);
	}

	addBreadcrumb(message, property) {
		Sentry.addBreadcrumb({
			category: 'ui.promptuary',
			message: message,
			data: property ?? {},
			level: 'info',
			type: 'default',
		});
	}

	throwError(message) {
		throw new Error(message);
	}
}
